import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AutomaticBillDetails from 'components/automatic-billiing/segments/AutomaticBillDetails';
import AutomaticBillRunPdfModal from 'components/automatic-billiing/segments/AutomaticBillRunPdfModal';
import FullScreenPageHeader from 'components/FullScreenPageHeader';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledActionButton from 'components/layout/StyledActionButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import moment from 'moment';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouterProps } from 'react-router';
import { Icon, Modal, Popup } from 'semantic-ui-react';
import { getAutomaticBillRunData, getAutomaticBillRunPdfData } from 'services/billServices';
import styled from 'styled-components';
import { AutomaticBillRunDto, AutomaticBillRunPdfResponse } from 'ts-types/api.types';
import { millisToMinutesAndSeconds } from 'utils/fomattingUtils';
import { noop } from 'utils/functionUtils';

const StyledPraxSegment = styled(PraxStyledSegment)`
  margin: 0 !important;
`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouterProps {
}

interface State {
  foundRuns: Array<AutomaticBillRunDto>,
  runsLoaded: boolean,
  modalOpen: boolean,
  automaticBillRun?: AutomaticBillRunDto,
  runId?: number,
  automaticBillRunPdfData?: AutomaticBillRunPdfResponse,
  billRunPdfModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
}

class AutomaticBillRunView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundRuns: [],
      runsLoaded: false,
      modalOpen: false,
      automaticBillRun: undefined,
      runId: undefined,
      billRunPdfModalOpen: false,
      cancelTokenSource
    };

    const onFinally = () => {
      this.setState({
        runsLoaded: true
      })
    };

    const mandateId = props.currentUser!.userConfig!.mandateId;
    getAutomaticBillRunData(mandateId, cancelTokenSource)
        .then(response =>
            this.setState({
              foundRuns: response
            })
        )
        .catch(noop)
        .finally(onFinally);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  };

  onOpenPdfClick = async (runId: number, automaticBillRun: AutomaticBillRunDto) => {

    const {cancelTokenSource} = this.state;

    await getAutomaticBillRunPdfData(runId, cancelTokenSource)
        .then(response => {
          this.setState({
            automaticBillRunPdfData: response,
            automaticBillRun: automaticBillRun
          })
        })
        .catch(noop)
        .finally(() => this.setState({billRunPdfModalOpen: true}));
  }

  openModal = (automaticBillRun: AutomaticBillRunDto | undefined) => {
    if (automaticBillRun) {
      this.goToAutomaticBillRun(automaticBillRun.id);
    }
  };

  showModal = (automaticBillRun: AutomaticBillRunDto) => {

    this.setState(({}: State) => {
      return {
        modalOpen: true,
        automaticBillRun: automaticBillRun,
        runId: automaticBillRun.id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      modalOpen: false,
      runId: undefined,
      automaticBillRun: undefined
    }));
  };

  closeAutomaticBillRunPdfModal = () => {

    this.setState({
      billRunPdfModalOpen: false,
      automaticBillRun: undefined,
      automaticBillRunPdfData: undefined
    });
  };


  goToAutomaticBilling = () => {
    const {history} = this.props;

    history.push("/automatic-billing");
  };


  goToAutomaticBillRun = (runId: number) => {
    const {history} = this.props;
    history.push(`/automatic-billing/run-data/${runId}`);
  };

  render(): React.ReactNode {
    const {automaticBillRun, runsLoaded, automaticBillRunPdfData, billRunPdfModalOpen} = this.state;
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <FullScreenPageHeader headerMessage={getMessage("automaticBilling.automaticBillingHistory")}
                                buttonMessage={getMessage("back")}
                                onConfirm={() => this.goToAutomaticBilling()}/>

          <StyledPraxSegment style={{height: "90%"}}>
            <StyledViewTableRow>
              {!runsLoaded
                  ? <LoaderComponent message={getMessage("automaticBilling.runLoader")}/>
                  : this.renderRunsSegment()}
            </StyledViewTableRow>
            {this.renderRunDataModal()}
          </StyledPraxSegment>
          {
            billRunPdfModalOpen && automaticBillRunPdfData !== undefined &&
            <AutomaticBillRunPdfModal
                modalOpen={billRunPdfModalOpen}
                onCloseModal={this.closeAutomaticBillRunPdfModal}
                automaticBillRun={automaticBillRun}
                automaticBillRunPdfData={automaticBillRunPdfData}
            />
          }
        </React.Fragment>

    );
  }

  dateCellRenderer = ({cellData}: any) => {
    if (moment(cellData).isValid()) {
      return <div>{moment(cellData).format("DD.MM.YYYY")}</div>
    }

    return "";
  }

  searchDateCellRenderer = ({rowData: {searchFromDate, searchToDate}}: any) => {
    const fromDate = moment(searchFromDate).isValid() ? moment(searchFromDate).format("DD.MM.YYYY") : ""
    const toDate = moment(searchToDate).isValid() ? moment(searchToDate).format("DD.MM.YYYY") : ""

    if (fromDate === "" && toDate === "") {
      return "";
    }

    return `${fromDate} - ${toDate}`;
  };

  timeOfExecutionCellRenderer = ({rowData: {timeOfExecution}}: any) => {
    if (timeOfExecution) {
      return <div>{moment(timeOfExecution).format("DD.MM.YYYY H:mm:ss")}</div>
    }
    return "";
  };

  responseTimeCellRenderer = ({rowData: {responseTime}}: any) => {
    if (responseTime) {

      return <div>{millisToMinutesAndSeconds(responseTime)}</div>
    }
    return "";
  };


  actionsCellRenderer = ({rowData: {index}, cellData}: any) => {
    const {getMessage} = this.props;
    const {foundRuns} = this.state;

    const automaticBillRun = foundRuns[index - 1];

    return (
        <React.Fragment>
          <StyledActionButton icon
                              type='button'
                              onClick={() => this.openModal(automaticBillRun)}
          >

            <Popup
                trigger={<Icon name={'file outline'}/>}
                content={getMessage("automaticBilling.openData")}
                size='small'
                position='top center'
                style={PopupStyle}
                inverted
            />
          </StyledActionButton>
          <StyledActionButton icon
                              type='button'
                              onClick={() => this.onOpenPdfClick(cellData, automaticBillRun)}
          >

            <Popup
                trigger={<Icon name={'file pdf outline'}/>}
                content={getMessage("automaticBilling.openPdf")}
                size='small'
                position='top center'
                style={PopupStyle}
                inverted
            />
          </StyledActionButton>
        </React.Fragment>
    );
  };


  runsRowGetter = ({index}: any) => {
    const {foundRuns} = this.state;

    Object.assign(foundRuns[index], {index: index + 1});

    return foundRuns[index];
  };

  runsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundRuns} = this.state;

    const automaticBillRun = foundRuns[index];

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openModal(automaticBillRun)}
        >
          {columns}
        </div>
    );
  };

  renderRunsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundRuns} = this.state;
    return (
        <VirtualizedTable
            rowCount={foundRuns.length}
            rowGetter={this.runsRowGetter}
            rowRenderer={this.runsRowRenderer}
            columns={[
              {
                width: 100,
                label: getMessage("id"),
                dataKey: "id"
              },
              {
                width: 250,
                label: (getMessage("user.name")),
                dataKey: "user"
              },
              {
                width: 150,
                label: (getMessage("bill.date")),
                dataKey: "billDate",
                cellRenderer: (this.dateCellRenderer)
              },
              {
                width: 300,
                label: (getMessage("automaticBilling.timeOfExecution")),
                dataKey: "timeOfExecution",
                cellRenderer: (this.timeOfExecutionCellRenderer)
              },
              {
                width: 200,
                label: (getMessage("automaticBilling.responseTime")),
                dataKey: "responseTime",
                cellRenderer: (this.responseTimeCellRenderer)
              },
              {
                width: 300,
                label: (`${getMessage("from")} - ${getMessage("to")}`),
                dataKey: "searchFromDate",
                cellRenderer: (this.searchDateCellRenderer)
              },
              {
                width: 600,
                label: (getMessage("tariffs")),
                dataKey: "searchTariffs"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />

    );
  };

  renderRunDataModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {modalOpen, automaticBillRun, runId} = this.state;

    return (
        <Modal
            open={modalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("automaticBilling.runData")}</Modal.Header>
          <Modal.Content>
            <AutomaticBillDetails
                automaticBillRun={automaticBillRun}
                runId={runId}
                onClose={this.hideModal}
            />
          </Modal.Content>
        </Modal>
    );
  };
}

export default withAuthContext(withMessages(AutomaticBillRunView));
